import { AxiosResponse } from 'axios';
import { BankAccount } from '../components/login/redux/types';
import api from './apiServices';

/**Login */
const login = (email: string, password: string) =>
    api.post('/user/investor/login', {
        email,
        password,
    });

/**Social Login */
const getSocialLoginResponse = (token: string) => api.get(`/user/federated/login-response?token=${token}`);
/**End Social Login */

/**Sign up*/
const signUp = (payload: Object) => api.post('/user/investor/signup', payload);

/**Forgot password */
const resendResetPasswordEmail = (payload: Object) => api.post(`/user/emailResetPassword`, payload);
const resetPassword = (payload: Object) => api.post(`/user/resetPassword`, payload);

/**Resend activation email */
const resendActivationEmail = (payload: Object) => api.post(`/user/emailActivateAccount`, payload);

/**Account Activation */
const activateAccount = (payload: Object) => api.post(`/user/activateAccount`, payload);

/**Investor Profile */
const getInvestorProfile = (investorUUID: string) => api.get(`/investor/${investorUUID}`);

/** User Profile */
const getUserProfile = (userUUID: string) => api.get(`/user/${userUUID}`);

/**User Profile Stepper */
const updateUserProfile = (investorUUID: string, payload: Object) => api.post(`/user/${investorUUID}/profile`, payload);
const updateAccreditation = (investorUUID: string, payload: Object) =>
    api.post(`/investor/${investorUUID}/accreditation`, payload);
const getNDAData = (investorUUID: string) =>
    api.post(`/investor/${investorUUID}/agreement/generate`, { agreementType: 'NDA' });
const confirmNDA = (investorUUID: string, payload: Object) =>
    api.post(`/investor/${investorUUID}/agreement/sign`, payload);

/**Deals */
const getDeals = () => api.get(`/deal/dealTeasers`);
const dealDetails = (uuid: string) => api.get(`/deal/investorDealDetails/${uuid}`);
const dealDataRoomReq = (dealUUID: string) => api.get(`/deal/${dealUUID}/deal-doc/DataRoom`);
const dealTermSheetReq = (dealUUID: string) => api.get(`deal/${dealUUID}/term-sheet`);
const dealWebinarReq = (dealUUID: string) => api.get(`/deal/${dealUUID}/webinar`);
const dealInvestmentInterest = (dealUUID: string, payload: Object) =>
    api.post(`/investment/${dealUUID}/interest`, payload);
const dealDisclosure = (dealUUID: string) => api.get(`/deal/disclosure-ack/${dealUUID}`);
const dealDisclosureConfirm = (dealUUID: string) => api.post(`/deal/disclosure-ack/${dealUUID}`);
/**Track Record */
const fetchTrackRecords = (dealUUID: string) => api.get(`/deal/fund/${dealUUID}/performance`);
/**Cashflow Simulator */
const fetchCashflowSimulator = (payload: Object) => api.post('/cashflows/deal-cashflow-simulator', payload);
/**Teaser */
const fetchTeaserDoc = (dealUUID: string) => api.get(`/deal/${dealUUID}/deal-doc/Teasers`);

/**Contact Us */
const dealContactUs = (dealUUID: string, payload: Object) =>
    api.post(`/investor/contact-us?dealUUID=${dealUUID}`, payload);
const generalContactUs = (payload: Object) => api.post(`/investor/contact-us`, payload);

/** Referrals */
const getReferralDetail = (uuid: string) => api.get(`/referral/${uuid}`);
const addReferral = (uuid: string, payload: Object) => api.post(`/referral/${uuid}`, payload);
const getIsEligibleReferrar = () => api.get(`/referral/eligible`);
const getUserNameByReferralCode = (referralCode: string) => api.get(`/user/name/${referralCode}`);
const inviteReferral = (investorUUID: string, payload: Object) => api.post(`/referral/invite/${investorUUID}`, payload);

/**Investments */
const getInvestments = () => api.get(`/v2/investment`);
const getInvestmentDetails = (uuid: string) => api.get(`/v2/investment/${uuid}/details`);
const getInvestmentStatus = () => api.get(`/investment/invested-status`);
const getExecutedAgreements = (uuid: string) => api.get(`/deal/${uuid}/deal-doc/ExecutedAgreements`);
const getUpdates = (uuid: string) => api.get(`/deal/${uuid}/deal-doc/Updates`);
/**Performances */
const fetchPerformaces = (uuid: string) => api.get(`/performance/investment/${uuid}/graph`);
/**Distributions */
const fetchDistributions = (investmentUUID: string) => api.get(`/v2/investment/${investmentUUID}/distribution`);
/**PPM */
const fetchPPM = (dealUUID: string, investorUUID: string) =>
    api.get(`/v2/deal/fund/${dealUUID}/ppm/investor/${investorUUID}`);
const downloadPPM = (dealUUID: string, investorUUID: string) =>
    api.get(`/v2/deal/fund/${dealUUID}/ppm/investor/${investorUUID}/download`, { responseType: 'arraybuffer' });
/**Reporting Obligation */
const fetchReportingObligation = (issuerUUID: string) => api.get(`/issuer/${issuerUUID}/reporting-obligations`);
const downloadReporting = (issuerUUID: string, reportUUID: string) =>
    api.get(`/issuer/${issuerUUID}/report/${reportUUID}`, { responseType: 'arraybuffer', timeout: Infinity });
/** Cashflow */
const fetchCashflow = (investmentUUID: string) => api.get(`/v2/investment/${investmentUUID}/cashflow`);
/** Guarantees */
const fetchGuarantees = (investmentUUID: string) => api.get(`/v2/investment/${investmentUUID}/guarantees`);
const fetchSecurities = (investmentUUID: string) => api.get(`/v2/investment/${investmentUUID}/securities`);
const fetchCovenants = (investmentUUID: string) => api.get(`/v2/investment/${investmentUUID}/covenants`);
/**Aggregate */
const fetchAggregate = () => api.get(`/v2/investment/aggregate`);
/**Update Docs */
const fetchInvestmentUpdatedDocs = () => api.get(`/v2/investment/reporting-obligations`);
/**Executed Agreements */
const fetchInvestmentExecutedAgreements = () => api.get(`/v2/investment/executed-agreements`);
const downloadExecuteOrUpdateDoc = (bookingUUID: string, docUUID: string) =>
    api.get(`/booking/${bookingUUID}/document/${docUUID}`, { responseType: 'arraybuffer' });
/**Deal Payment */
const dealPaymentReq = (uuid: string) => api.get(`/investment/${uuid}`);
const downloadPaymentReceipt = (payload: Object, iUUID: string) =>
    api.post(`/investment/${iUUID}/document/download`, payload, { responseType: 'arraybuffer' });
const withdraw = (investmentUUID: string) => api.post(`/investment/${investmentUUID}/cancel`, {});
const uploadPaymentReceipt = (payload: Object, iUUID: string) =>
    api.post(`/investment/${iUUID}/payment-receipt/upload`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });

/**Repayemnt */
const fetchRepayment = (investmentUUID: string) => api.get(`/repayment/investment/${investmentUUID}`);
const fetchInvestmentRepayment = (investmentUUID: string) => api.get(`/investment/${investmentUUID}/repayment`);

/** DataRoom */
const downloadDataRoomDocument = (dealUUID: string, parentUUID: string, uuid: string, group: string) =>
    api.get(`/deal/${dealUUID}/deal-doc/${group}/${parentUUID}/${uuid}`, { responseType: 'arraybuffer' });
const downloadZipFile = (dealUUID: string, group: string, rootUUID: string) =>
    api.get(`/deal/${dealUUID}/deal-doc/${group}/${rootUUID}`, { responseType: 'arraybuffer' });

/** Analytics Tracks */
const analyticsTrackUpdate = (dealUUID: string, statsUUID: string) =>
    api.post(`/deal/${dealUUID}/stats/update`, { statsUUID });
const analyticsTrackStart = (dealUUID: string, docUUID: string, event: string) =>
    api.post(`/deal/${dealUUID}/stats/start`, {
        event,
        eventEntityUUID: docUUID,
    });

/** Announcements */
const getAnnouncements = () => api.get(`/announcements`);

/* Settings */
interface BARes {
    bankAccounts: BankAccount[];
}
const saveAndUpdateBankDetails = (payload: BankAccount, investorUUID: string): Promise<AxiosResponse<BARes, number>> =>
    api.post(`/investor/${investorUUID}/bank-account`, payload);

const deleteBank = (bankUUID: string, investorUUID: string): Promise<AxiosResponse<BARes, number>> =>
    api.delete(`/investor/${investorUUID}/bank-account`, {
        data: {
            bankAccountUUID: bankUUID,
        },
    });

const updatePassword = (oldPassword: string, password: string) =>
    api.post('/user/updatePassword', { oldPassword, password });
const downloadFile = (investorUUID: string, agreementUUID: string) =>
    api.get(`/investor/${investorUUID}/agreement/${agreementUUID}`, { responseType: 'arraybuffer' });
const saveKYC = (investorUUID: string, payload: any) => api.post(`/investor/${investorUUID}/kyc`, payload);
const KYCDocumentDownload = (investorUUID: string, docUUID: string) =>
    api.get(`/investor/${investorUUID}/kyc/doc/${docUUID}`, { responseType: 'arraybuffer' });
const uploadKYCDocument = (investorUUID: string, payload: FormData) =>
    api.post(`/investor/${investorUUID}/kyc/doc/upload`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
const deleteKYCDoc = (investerUUID: string, docUUID: string) =>
    api.delete(`/investor/${investerUUID}/kyc/doc`, { data: { kycDocUUIDs: [docUUID] } });
const KYCSubmitReq = (investorUUID: string, payload: Object) =>
    api.post(`/investor/${investorUUID}/kyc/submit`, payload);

/**Agreements */
const fetchAgreementHtml = (investorUUID: string, payload: Object) =>
    api.post(`/investor/${investorUUID}/agreement/generate`, payload);
const signAgreement = (
    investorUUID: string,
    agreementType: 'AIConsent' | 'SubscriptionAgreement' | 'HIPL AI Consent',
    agreementUUID?: string,
) => api.post(`/investor/${investorUUID}/agreement/sign`, { agreementType: agreementType, agreementUUID, skip: false });

//Investor Profile
const updateInvestorProfile = (investorUUID: string, payload: Object) =>
    api.post(`/investor/${investorUUID}/profile`, payload);

//Investment Preferences
const updateInvestorPreferences = (investorUUID: string, payload: Object) =>
    api.post(`/investor/${investorUUID}/preferences`, payload);

const checkAccreditationAllow = (investorUUID: string) =>
    api.get(`/investor/${investorUUID}/accreditation/is-editable`);

//Account Activation with otp
const sendOTP = (payload: Object) => api.post('/user/activate/send-otp', payload);
const verifyOTP = (payload: Object) => api.post(`/user/activate/verify-otp`, payload);

//Myinfo
const redirectToMyinfo = (investorUUID: string, noa: boolean) =>
    api.get(`/investor/kyc/myinfo/redirect?investorUUID=${investorUUID}&noa=${noa}`);

//Constants
const fetchConstants = () => api.get('/constants');

//download historical deals
const downloadHistoricalDeals = () =>
    api.get(`/property/file/Misc/HistoricalDealSheet`, { responseType: 'arraybuffer' });

//AI
const AIFileUpload = (investorUUID: string, payload: Object) =>
    api.post(`/investor/${investorUUID}/accreditation/document/upload`, payload);
const deleteAIFile = (investorUUID: string, fileUUID: string) =>
    api.delete(`/investor/${investorUUID}/accreditation/document/${fileUUID}/delete `);
const AISubmit = (investorUUID: string, payload: Object) =>
    api.post(`/investor/${investorUUID}/accreditation/submit`, payload);
const AIDocDownload = (investorUUID: string, docUUID: string) =>
    api.get(`/investor/${investorUUID}/accreditation/document/${docUUID}/download`, { responseType: 'arraybuffer' });

/* Team Management */
const createTeam = (investorUUID: string) => api.post(`/investor/${investorUUID}/team`);
const inviteTeamMember = (investorUUID: string, payload: Object) =>
    api.put(`/investor/${investorUUID}/team-members`, payload);
const getTeamMember = (investorUUID: string) => api.get(`/investor/${investorUUID}/team-members`);
const deleteTeamMember = (investorUUID: string, userUUID: string) =>
    api.delete(`/investor/${investorUUID}/team-members/${userUUID}`);
const updateTeamMembers = (investorUUID: string, userUUID: string, role: string) =>
    api.put(`/investor/${investorUUID}/team-members/update-role`, { memberUUID: userUUID, role });

const investorByTeam = () => api.get('/investor-by-team');

const Apis = {
    login,
    signUp,
    resendActivationEmail,
    activateAccount,
    updateUserProfile,
    updateAccreditation,
    getNDAData,
    confirmNDA,
    getDeals,
    dealDetails,
    dealDataRoomReq,
    dealTermSheetReq,
    dealWebinarReq,
    dealContactUs,
    generalContactUs,
    getInvestments,
    getInvestmentStatus,
    getExecutedAgreements,
    getUpdates,
    dealPaymentReq,
    saveAndUpdateBankDetails,
    deleteBank,
    updatePassword,
    downloadPaymentReceipt,
    withdraw,
    downloadFile,
    saveKYC,
    uploadPaymentReceipt,
    fetchRepayment,
    KYCDocumentDownload,
    uploadKYCDocument,
    deleteKYCDoc,
    analyticsTrackStart,
    downloadDataRoomDocument,
    analyticsTrackUpdate,
    getAnnouncements,
    fetchAgreementHtml,
    signAgreement,
    dealInvestmentInterest,
    downloadZipFile,
    KYCSubmitReq,
    resendResetPasswordEmail,
    resetPassword,
    getInvestorProfile,
    getReferralDetail,
    addReferral,
    getIsEligibleReferrar,
    getUserNameByReferralCode,
    getUserProfile,
    getSocialLoginResponse,
    updateInvestorProfile,
    updateInvestorPreferences,
    checkAccreditationAllow,
    dealDisclosure,
    dealDisclosureConfirm,
    sendOTP,
    verifyOTP,
    redirectToMyinfo,
    fetchConstants,
    inviteReferral,
    getInvestmentDetails,
    fetchInvestmentRepayment,
    fetchPerformaces,
    fetchDistributions,
    fetchTrackRecords,
    fetchPPM,
    downloadPPM,
    downloadHistoricalDeals,
    fetchReportingObligation,
    downloadReporting,
    fetchCashflow,
    fetchGuarantees,
    fetchSecurities,
    fetchCovenants,
    fetchAggregate,
    fetchInvestmentUpdatedDocs,
    AIFileUpload,
    deleteAIFile,
    fetchCashflowSimulator,
    fetchInvestmentExecutedAgreements,
    downloadExecuteOrUpdateDoc,
    fetchTeaserDoc,
    AISubmit,
    AIDocDownload,
    createTeam,
    inviteTeamMember,
    getTeamMember,
    deleteTeamMember,
    updateTeamMembers,
    investorByTeam,
};

export default Apis;
